import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { api, euro, type SnapshotDto } from '../lib/api';
import { lampFor } from '../lib/anomaly';
import { ApiError } from '../lib/api';

export function OverviewPage() {
  const [snapshots, setSnapshots] = useState<SnapshotDto[] | null>(null);
  const [mock, setMock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .kpis()
      .then((r) => {
        setSnapshots(r.snapshots);
        setMock(r.mock);
        setError(null);
      })
      .catch((e: unknown) => {
        setSnapshots([]);
        setError(
          e instanceof ApiError && e.status === 403
            ? 'Für den Sales Manager fehlt dir die Rolle admin. Ein Hub-Administrator kann sie vergeben.'
            : e instanceof ApiError
              ? e.message
              : 'Daten konnten nicht geladen werden.',
        );
      });
  }, []);

  useEffect(load, [load]);

  const refresh = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api.refresh();
      setNote(`Aktualisiert: ${r.members} Personen, Stand ${r.date}.`);
      load();
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'Aktualisieren fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container stack">
      <div className="section__head">
        <div>
          <div className="eyebrow">// sales manager</div>
          <h1>Übersicht</h1>
        </div>
        <button className="px-btn px-btn--sm" onClick={refresh} disabled={busy}>
          <RefreshCw size={14} className={busy ? 'spin' : undefined} />
          {busy ? 'Aktualisiert…' : 'Aktualisieren'}
        </button>
      </div>

      {mock && (
        <div className="alert alert--info">
          <strong>Beispieldaten.</strong> Der CRM-Zugang ist noch nicht hinterlegt
          (<span className="mono">ZOHO_CRM_REFRESH_TOKEN</span>). Alles, was hier steht, ist
          erfunden – keine Zahl daraus taugt für eine Entscheidung.
        </div>
      )}
      {note && <div className="alert alert--success">{note}</div>}
      {error && <div className="alert alert--error">{error}</div>}

      {snapshots === null && <p className="card__meta">Wird geladen…</p>}
      {snapshots?.length === 0 && !error && (
        <div className="empty">
          Noch keine Snapshots. Mit „Aktualisieren" den ersten Lauf starten – oder auf die
          nächtliche Aktualisierung warten.
        </div>
      )}

      <div className="sm-grid">
        {(snapshots ?? []).map((s) => {
          const lamp = lampFor(s.payload);
          return (
            <Link to={`/ma/${encodeURIComponent(s.zohoUserId)}`} key={s.zohoUserId} className="card">
              <div className="sm-card__top">
                <span className="sm-card__name">{s.maName}</span>
                <span className={`sm-lamp sm-lamp--${lamp.level}`}>
                  <span className="sm-lamp__dot" />
                  {lamp.reason}
                </span>
              </div>

              <div className="sm-card__kpis">
                <div>
                  <div className="sm-kpi__label">offene Deals</div>
                  <div className="sm-kpi__value mono">{s.payload.openDealCount}</div>
                </div>
                <div>
                  <div className="sm-kpi__label">offene Leads</div>
                  <div className="sm-kpi__value mono">{s.payload.openLeadCount}</div>
                </div>
                <div>
                  <div className="sm-kpi__label">gewonnen, Monat</div>
                  <div className="sm-kpi__value mono">
                    {s.payload.wonMonth.count} {s.payload.wonMonth.count === 1 ? 'Deal' : 'Deals'}
                  </div>
                  <div className="card__meta">Wert: {euro(s.payload.wonMonth.amount)}</div>
                </div>
                <div>
                  <div className="sm-kpi__label">verloren, Monat</div>
                  <div className="sm-kpi__value mono">
                    {s.payload.lostMonth.count} {s.payload.lostMonth.count === 1 ? 'Deal' : 'Deals'}
                  </div>
                  <div className="card__meta">Notizen diese Woche: {s.payload.notesWeek}</div>
                </div>
              </div>

              <div className="card__meta" style={{ marginTop: '0.6rem' }}>
                Stand {s.snapshotDate}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
